import {
  ConversionPermissions,
  CustomersPermissions,
  ImsPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
  SupportPermissions,
} from "@redotech/account-common/permissions";
import { registerSoleStoreSignInRoots } from "@redotech/redo-merchant-app-common/auth";
import { CATALOG_DATA_PERMISSION } from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { loginDestinationRoutes } from "@redotech/redo-merchant-app-common/routing/login-destination-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import {
  registerAuthFlowRoots,
  registerStoreBoundAuthFlowRoots,
} from "@redotech/redo-merchant-app-common/routing/store-scope";
import { approvalRoutes } from "@redotech/redo-merchant-general-app/approvals/route";
import { oauthRoutes } from "@redotech/redo-merchant-login-app/oauth/route";
import {
  loginRoutes,
  signupRoutes,
} from "@redotech/redo-merchant-login-app/route";
import { Navigate } from "react-router-dom";
import { amazonRoutes } from "./amazon/route";
import { askRedoRoutes } from "./app/ask-redo/routes/ask-redo-routes";
import { aeoRoutes } from "./app/navigation/routing/aeo-routes";
import { analyticsRoutes } from "./app/navigation/routing/analytics-routes";
import { catalogRoutes } from "./app/navigation/routing/catalog-routes";
import { conversionRoutes } from "./app/navigation/routing/conversion-routes";
import { customersRoutes } from "./app/navigation/routing/customers-routes";
import { developerRoutes } from "./app/navigation/routing/developer-routes";
import { homeRoutes } from "./app/navigation/routing/home-routes";
import { inventoryRoutes } from "./app/navigation/routing/inventory-routes";
import {
  defaultChildRoute,
  LEGACY_REDIRECTS,
  legacyRedirectRoutes,
} from "./app/navigation/routing/legacy-redirects";
import { marketingRoutes } from "./app/navigation/routing/marketing-routes";
import { paymentsRoutes } from "./app/navigation/routing/payments-routes";
import { redoSuppliesRoutes } from "./app/navigation/routing/redo-supplies-routes";
import { returnsRoutes } from "./app/navigation/routing/returns-routes";
import { settingRoutes } from "./app/navigation/routing/settings-routes";
import { shippingRoutes } from "./app/navigation/routing/shipping-routes";
import { shopperAgentRoutes } from "./app/navigation/routing/shopper-agent-routes";
import { supportRoutes } from "./app/navigation/routing/support-routes";
import { supportV2Routes } from "./app/navigation/routing/support-v2-routes";
import { Page } from "./app/page";
import { appleMessagesRoutes } from "./apple-messages/route";
import { bigcommerceRoutes } from "./bigcommerce/route";
import {
  marketingCommentSoldRoutes,
  omsCommentSoldRoutes,
} from "./commentsold/route";
import { faireCallbackRedirectRoute, faireRoutes } from "./faire/route";
import { gorgiasRoutes } from "./gorgias/route";
import { OnboardingRedirect } from "./onboarding/redirect";
import { paypalRoutes } from "./paypal/route";
import { MerchantApp404Page } from "./platform/merchant-app-404-page";
import { riseRoutes } from "./rise.ai/route";
import { rithumRoutes } from "./rithum/route";
import { RouterRoot } from "./root";
import { shopifyRoutes } from "./shopify/route";
import "./styles.css";
import { temuRoutes } from "./temu/route";
import { tiktokRoutes } from "./tiktok/route";
import { uspsRoutes } from "./usps/route";
import { walmartRoutes } from "./walmart/route";
import { welcomeRoutes } from "./welcome/route";

const TIKTOK_ROOT = "tiktok";

/** Routes entered from outside a store session: login, install and OAuth flows, public pages. Never store-scoped. */
const authFlowRoutes: PermissionedRouteObject[] = [
  { path: "onboarding-redirect", element: <OnboardingRedirect /> },
  {
    path: "intent",
    async lazy() {
      const { IntentLandingPage } =
        await import("@redotech/redo-merchant-conversion-app/conversion-ad/ad-landing-page");
      return { Component: IntentLandingPage };
    },
  },
  // Old combined PPU-intent ad URL. Send it through the intent landing.
  { path: "ppu-intent", element: <Navigate replace to="/intent" /> },
  { path: "login", children: loginRoutes },
  { path: "signup", children: signupRoutes },
  { path: "welcome", children: welcomeRoutes },
  { path: loginDestinationRoutes.oauth.root, children: oauthRoutes },
  {
    path: "sms-terms",
    lazy: async () => {
      const { SmsTermsPage } = await import("./sms-program-policy-page");
      return { Component: SmsTermsPage };
    },
  },
  {
    path: "sms-privacy",
    lazy: async () => {
      const { SmsPrivacyPage } = await import("./sms-program-policy-page");
      return { Component: SmsPrivacyPage };
    },
  },
  { path: "shopify", children: shopifyRoutes },
  { path: "bigcommerce", children: bigcommerceRoutes },
  { path: loginDestinationRoutes.gorgias.root, children: gorgiasRoutes },
  { path: "rise-ai", children: riseRoutes },
  { path: TIKTOK_ROOT, children: tiktokRoutes },
  { path: "rithum", children: rithumRoutes },
  { path: "temu", children: temuRoutes },
  { path: "walmart", children: walmartRoutes },
  { path: "amazon", children: amazonRoutes },
  { path: "apple-messages", children: appleMessagesRoutes },
  { path: "oms-commentsold", children: omsCommentSoldRoutes },
  { path: "marketing-commentsold", children: marketingCommentSoldRoutes },
  { path: "usps", children: uspsRoutes },
  { path: "faire", children: faireRoutes },
  { path: "paypal", children: paypalRoutes },
];
registerAuthFlowRoots(
  authFlowRoutes.flatMap((route) => (route.path ? [route.path] : [])),
);
// These flows connect a store, so login must land them in one.
registerStoreBoundAuthFlowRoots([
  TIKTOK_ROOT,
  loginDestinationRoutes.gorgias.root,
]);

const VERIFY_IDENTITY_ROOT = "verify-identity";

/** Opened from Ask Redo sign-in links; needs a store session, so login scopes it into the chosen store. */
const verifyIdentityRoute: PermissionedRouteObject = {
  path: VERIFY_IDENTITY_ROOT,
  element: (
    <Page
      hideHeader={() => true}
      hideNavbar={() => true}
      hideSearchBarAndBanner={() => true}
    />
  ),
  children: [
    {
      index: true,
      async lazy() {
        const { VerifyIdentityPage } =
          await import("./verify-identity/verify-identity-page");
        return { Component: VerifyIdentityPage };
      },
    },
  ],
};
registerSoleStoreSignInRoots([VERIFY_IDENTITY_ROOT, TIKTOK_ROOT]);

const children: PermissionedRouteObject[] = [
  defaultChildRoute("home"),
  { path: "home", children: homeRoutes },
  ...askRedoRoutes,
  { path: "aeo", children: aeoRoutes },
  { path: "analytics", children: analyticsRoutes },
  { path: "developer", children: developerRoutes },
  {
    path: "support",
    children: supportRoutes,
    handle: { permission: SupportPermissions.VIEW_SUPPORT },
  },
  {
    path: "support-v2",
    children: supportV2Routes,
    handle: { permission: SupportPermissions.VIEW_SUPPORT },
  },
  { path: "commerce-agent", children: shopperAgentRoutes },
  {
    path: "marketing",
    children: marketingRoutes,
    handle: { permission: MarketingPermissions.VIEW_MARKETING },
  },

  {
    path: "conversion",
    children: conversionRoutes,
    handle: { permission: ConversionPermissions.ACCESS_CONVERSION },
  },

  {
    path: "shipping",
    children: shippingRoutes,
    handle: {
      permission: [
        OrderManagementPermissions.VIEW_ORDER,
        OrderManagementPermissions.SCAN_AND_VERIFY,
        OrderManagementPermissions.MANAGE_SHIPMENTS,
        OrderManagementPermissions.MANAGE_PICK_SESSIONS,
      ],
    },
  },
  {
    path: "returns",
    children: returnsRoutes,
    handle: {
      permission: [
        ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
        ReturnsAndClaimsPermissions.VIEW_RETURN,
        ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
        ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
        ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
        ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
      ],
    },
  },
  {
    path: "customer",
    children: customersRoutes,
    handle: { permission: CustomersPermissions.VIEW_CUSTOMER_RECORD },
  },
  { path: "payments", children: paymentsRoutes },
  { path: "redo-supplies", children: redoSuppliesRoutes },

  {
    path: "inventory",
    children: inventoryRoutes,
    handle: { permission: Object.values(ImsPermissions) },
  },
  {
    path: "catalog",
    children: catalogRoutes,
    handle: { permission: CATALOG_DATA_PERMISSION },
  },
  {
    path: "order-management/approvals",
    children: approvalRoutes,
    handle: { permission: [ReturnsAndClaimsPermissions.VIEW_RETURN] },
  },
  { path: "settings", children: settingRoutes },

  ...authFlowRoutes,
  verifyIdentityRoute,
  ...legacyRedirectRoutes(LEGACY_REDIRECTS, () => routes),
];

export const routes: PermissionedRouteObject[] = [
  faireCallbackRedirectRoute,
  {
    path: "/stores/:teamId/",
    Component: RouterRoot,
    children,
    errorElement: <MerchantApp404Page />,
  },
  {
    path: "/",
    Component: RouterRoot,
    errorElement: <MerchantApp404Page />,
    children,
  },
];
